import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import Splash from "@/components/Splash";
import PropertyCard from "@/components/PropertyCard";
import Marquee from "@/components/Marquee";
import InstallPrompt from "@/components/InstallPrompt";
import PushManager from "@/components/PushManager";
import AdsSection from "@/components/AdsSection";
import HomeShowcase from "@/components/HomeShowcase";
import LoanProviderStrip from "@/components/LoanProviderStrip";
import VipLaunchBanner from "@/components/VipLaunchBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, ShieldCheck, MapPinned, Users, PartyPopper, UtensilsCrossed, Sparkles, ArrowRight, Loader2 } from "lucide-react";

const AP_DISTRICTS = ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool", "Rajahmundry", "Tirupati", "Kakinada", "Kadapa", "Anantapur", "Vizianagaram", "Eluru", "Ongole", "Srikakulam", "Machilipatnam", "Chittoor", "Nandyal", "Amaravati"];

const HERO = "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(!sessionStorage.getItem("mp_splash"));
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const [nearbyOpen, setNearbyOpen] = useState(false);
  const [nearbyArea, setNearbyArea] = useState("all");
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [comingSoon, setComingSoon] = useState(false);

  const goNearby = async () => {
    setComingSoon(false);
    if (nearbyArea === "all") { setNearbyOpen(false); navigate("/browse"); return; }
    setNearbyLoading(true);
    try {
      const { data } = await api.get("/properties", { params: { status: "available", q: nearbyArea } });
      if ((data.properties || []).length > 0) { setNearbyOpen(false); navigate(`/browse?q=${encodeURIComponent(nearbyArea)}`); }
      else setComingSoon(true);
    } catch (e) { setComingSoon(true); } finally { setNearbyLoading(false); }
  };

  useEffect(() => {
    if (showSplash) sessionStorage.setItem("mp_splash", "1");
    api.get("/properties", { params: { status: "available", featured: true } }).then((r) => {
      const feat = r.data.properties;
      if (feat.length) setFeatured(feat.slice(0, 6));
      else api.get("/properties", { params: { status: "available" } }).then((r2) => setFeatured(r2.data.properties.slice(0, 6)));
    });
  }, []);

  const search = () => navigate(`/browse${q ? `?q=${encodeURIComponent(q)}` : ""}`);

  const services = [
    { icon: PartyPopper, key: "services.events" },
    { icon: UtensilsCrossed, key: "services.catering" },
    { icon: Sparkles, key: "services.decorators" },
  ];

  return (
    <div>
      {showSplash && <Splash onDone={() => setShowSplash(false)} />}

      <Marquee />

      {/* HERO */}
      <VipLaunchBanner />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-emerald/95 via-brand-emerald/80 to-brand-emerald/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-5 sm:py-6 min-h-[24vh] sm:min-h-[26vh] flex items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white text-xs font-medium px-3 py-1 rounded-full mb-3 uppercase tracking-widest">
              <ShieldCheck className="w-4 h-4" /> mypropertyas.com
            </span>
            <h1 className="font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              {t("hero.title")}
            </h1>
            <p className="text-white/80 text-sm sm:text-base mt-2 max-w-xl leading-relaxed">{t("hero.sub")}</p>

            <div className="mt-4 bg-white rounded-2xl p-2 flex items-center gap-2 max-w-xl shadow-2xl">
              <Search className="w-5 h-5 text-brand-muted ml-3 shrink-0" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder={t("hero.search")}
                data-testid="hero-search-input"
                className="border-0 shadow-none focus-visible:ring-0 text-brand-ink"
              />
              <Button onClick={search} data-testid="hero-search-btn" className="rounded-xl bg-brand-terracotta hover:bg-brand-terracottaHover shrink-0">
                {t("filter.search")}
              </Button>
            </div>

            <div className="flex flex-wrap gap-3 mt-4">
              <Button onClick={() => navigate("/browse")} className="rounded-full bg-white text-brand-emerald hover:bg-white/90" data-testid="hero-explore-btn">
                {t("hero.cta")} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button onClick={() => navigate("/browse?near=1")} variant="outline" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10" data-testid="hero-near-btn">
                <MapPinned className="w-4 h-4 mr-1.5" /> {t("hero.near")}
              </Button>
              <InstallPrompt />
              <PushManager />
            </div>
          </motion.div>
        </div>
      </section>

      {/* SHOWCASE: Offers + Carousel + Card Banners */}
      <HomeShowcase />

      {/* Loan provider strip */}
      <div className="mt-8"><LoanProviderStrip /></div>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-14">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <h2 className="font-heading font-bold text-brand-ink text-2xl sm:text-3xl lg:text-4xl tracking-tight">{t("featured")}</h2>
            <p className="text-brand-muted mt-2">{t("featured.sub")}</p>
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/browse")} data-testid="view-all-btn">
            {t("nav.browse")} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p) => <PropertyCard key={p.id} property={p} />)}
        </div>
      </section>

      {/* ADVERTISEMENTS */}
      <AdsSection />

      {/* FUTURE SERVICES */}
      <section className="bg-brand-emerald text-white relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">{t("services.title")}</h2>
            <p className="text-white/70 mt-3">{t("services.sub")}</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {services.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center backdrop-blur hover:bg-white/10 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <s.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white">{t(s.key)}</h3>
                <span className="inline-block mt-3 text-xs uppercase tracking-widest text-brand-terracotta font-semibold">{t("services.soon")}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEARBY INSIGHTS LOCATION SELECTOR */}
      <Dialog open={nearbyOpen} onOpenChange={setNearbyOpen}>
        <DialogContent data-testid="nearby-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPinned className="w-5 h-5 text-brand-emerald" /> Explore by Location</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-brand-muted">Pick a district/city to see available properties there.</p>
            <Select value={nearbyArea} onValueChange={(v) => { setNearbyArea(v); setComingSoon(false); }}>
              <SelectTrigger data-testid="nearby-area-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="nearby-area-all">All Locations</SelectItem>
                {AP_DISTRICTS.map((d) => <SelectItem key={d} value={d} data-testid={`nearby-area-${d}`}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            {comingSoon && (
              <div className="rounded-xl bg-brand-lease/10 text-brand-lease px-4 py-4 text-center font-medium" data-testid="coming-soon-msg">
                We are Coming Soon to Your Area!
              </div>
            )}
            <Button onClick={goNearby} disabled={nearbyLoading} className="w-full rounded-full bg-brand-emerald" data-testid="nearby-go-btn">
              {nearbyLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
              {nearbyArea === "all" ? "Browse All Properties" : "View Properties"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
